import React, { useState, useEffect } from "react";
import TextInputComp from "../TextInputComp";
import CommonButton from "../../CommonButton";
import { MapPin, Check, Edit2 } from "lucide-react";
import { ImagePath } from "../../../assests";
import { useLazyGetCheckoutSummaryQuery } from "../../../redux/actions/Customer/cartCustomer";
import { useCreateAddressMutation, useUpdateAddressMutation } from "../../../redux/actions/Customer/profileCustomer";
import { IAddressRequest } from "../../../types/Request/CustomerRequest";

interface Address {
  id?: number; 
  fullName: string;
  phoneNumber: string;
  addressLine1: string;
  addressLine2?: string;
  city: string;
  state?: string;
  country?: string;
  postalCode?: string;
  latitude?: number;
  longitude?: number;
  addressType?: string;
  isDefault?: boolean;
}

interface AddressCardProps {
  initialAddress?: Address | null;
  onCancelForm?: () => void;
  onSuccess?: () => void; // Added callback to support automatic parent refresh on creation
}

const AddressCard: React.FC<AddressCardProps> = ({ initialAddress, onCancelForm, onSuccess }) => {
  const [showForm, setShowForm] = useState(!initialAddress);
  const [isEditing, setIsEditing] = useState(false);

  const [fullName, setFullName] = useState("");
  const [addressLine1, setAddressLine1] = useState("");
  const [addressLine2, setAddressLine2] = useState("");
  const [city, setCity] = useState("");
  const [postalCode, setPostalCode] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [addressType, setAddressType] = useState<string>('Home');

  const [triggerRecalculateSummary] = useLazyGetCheckoutSummaryQuery();
  const [saveNewAddressToServer, { isLoading: isCreatingAddress }] = useCreateAddressMutation();
  const [updateAddressOnServer, { isLoading: isUpdatingAddress }] = useUpdateAddressMutation();

  useEffect(() => {
    if (!initialAddress) {
      setShowForm(true);
      setIsEditing(false);
    } else {
      setShowForm(false);
    }
  }, [initialAddress]);

  const handlePopulateFormForEdit = () => {
    if (!initialAddress) return;
    setFullName(initialAddress.fullName || "");
    setAddressLine1(initialAddress.addressLine1 || "");
    setAddressLine2(initialAddress.addressLine2 || "");
    setCity(initialAddress.city || "");
    setPostalCode(initialAddress.postalCode || "");
    setPhoneNumber(initialAddress.phoneNumber || "");
    setAddressType(initialAddress.addressType || 'Home');
    setIsEditing(true);
    setShowForm(true);
  };

  const handleFakeLocationPinSelect = () => {
    setAddressLine1("Sheikh Zayed Road, Downtown Dubai");
    setAddressLine2("Burj Khalifa District");
    setCity("Dubai");
    setPostalCode("00000");
  };

  const handleResetFormStateFields = () => {
    setFullName("");
    setAddressLine1("");
    setAddressLine2("");
    setCity("");
    setPostalCode("");
    setPhoneNumber("");
    setAddressType('Home');
    setIsEditing(false);
    
    if (onCancelForm) {
      onCancelForm();
    } else {
      setShowForm(false);
    }
  };

  const handleSaveOrUpdateAddressForm = async () => {
    if (!fullName.trim() || !addressLine1.trim() || !phoneNumber.trim() || !city.trim()) {
      alert("Please complete all structural validation requirement target fields.");
      return;
    }

    const addressPayload: IAddressRequest = {
      fullName,
      phoneNumber,
      addressLine1,
      addressLine2,
      city,
      state: initialAddress?.state || city,
      country: initialAddress?.country || "United Arab Emirates",
      postalCode: postalCode || "00000",
      latitude: initialAddress?.latitude || 25.2048, 
      longitude: initialAddress?.longitude || 55.2708,
      addressType: addressType,
      isDefault: initialAddress?.isDefault ?? true,
    };

    try {
      if (isEditing && initialAddress?.id) {
        await updateAddressOnServer({ 
          addressId: initialAddress.id, 
          data: addressPayload 
        }).unwrap();
      } else {
        await saveNewAddressToServer(addressPayload).unwrap();
      }

      await triggerRecalculateSummary().unwrap();
      
      if (onSuccess) {
        onSuccess();
      } else {
        handleResetFormStateFields();
      }
    } catch (err) {
      console.error("Failed to persist address coordinates alteration layout records:", err);
    }
  };

  const isFormSubmitting = isCreatingAddress || isUpdatingAddress;

  return (
    <div className="text-left relative w-full">
      {isFormSubmitting && (
        <div className="absolute inset-0 bg-white/50 backdrop-blur-3xs z-30 flex items-center justify-center" />
      )}

      {initialAddress && !showForm && (
        <div className="border border-slate-200/80 rounded-2xl p-4 bg-white relative shadow-3xs border-l-4 border-l-[#2F5E2E]">
          <span className="absolute top-3 right-3 flex items-center gap-1 text-[10px] font-black tracking-wider bg-emerald-50 text-emerald-700 px-2.5 py-1 border border-emerald-100 rounded-full uppercase">
            <Check size={11} className="stroke-[3]" /> Bound Coordinate
          </span>

          <div className="space-y-1 pr-24">
            <div className="flex items-center gap-2">
              <p className="font-bold text-slate-900 text-sm">{initialAddress.fullName}</p>
              <span className="px-1.5 py-0.5 rounded bg-slate-100 text-[9px] text-slate-500 font-bold uppercase">
                {initialAddress.addressType === 'Office' ? "Office" : "Home"}
              </span>
            </div>
            <p className="text-xs text-slate-600 leading-relaxed mt-1">
              {initialAddress.addressLine1} {initialAddress.addressLine2 && `, ${initialAddress.addressLine2}`}
            </p>
            <p className="text-xs text-slate-500 font-semibold">
              {initialAddress.city}, UAE {initialAddress.postalCode && `(${initialAddress.postalCode})`}
            </p>
            <p className="text-xs text-slate-400 font-semibold pt-1">
              Mobile: <span className="font-mono text-slate-600 font-bold">{initialAddress.phoneNumber}</span>
            </p>
          </div>

          <div className="flex gap-2.5 mt-4 border-t border-slate-50 pt-3">
            <CommonButton
              text="Modify Details"
              variant="outline"
              rounded="md"
              icon={<Edit2 size={12} />}
              iconPosition="left"
              onClick={handlePopulateFormForEdit}
              className="h-8 text-xs font-black px-4"
            />
          </div>
        </div>
      )}

      {showForm && (
        <div className="bg-white p-5 border border-slate-200 rounded-2xl space-y-4 shadow-3xs">
          <p className="text-xs font-black text-slate-400 uppercase tracking-wider">
            {isEditing ? "Modify Coordinates Profile" : "Create New Address Profile"}
          </p>

          <div 
            className="relative w-full h-36 rounded-xl overflow-hidden mb-2 cursor-pointer border shadow-inner" 
            onClick={handleFakeLocationPinSelect}
          >
            <img src={ImagePath.DubaiPay} alt="Map UI Canvas" className="w-full h-full object-cover opacity-80" />
            <div className="absolute inset-0 bg-black/5 flex flex-col items-center justify-center gap-1">
              <MapPin size={24} className="text-rose-500 drop-shadow-sm animate-bounce" />
              <span className="text-[10px] bg-white px-2.5 py-1 rounded-xl font-bold text-slate-700">
                Click to autofill mock telemetry coordinates
              </span>
            </div>
          </div>

          <TextInputComp showLabel label="Receiver Full Name" value={fullName} onChangeText={setFullName} />
          <TextInputComp showLabel label="Street Address / Building Line" value={addressLine1} onChangeText={setAddressLine1} />
          <TextInputComp showLabel label="Apartment, Suite, Unit (Optional)" value={addressLine2} onChangeText={setAddressLine2} />
          
          <div className="grid grid-cols-2 gap-3">
            <TextInputComp showLabel label="City" value={city} onChangeText={setCity} />
            <TextInputComp showLabel label="Postal Code" value={postalCode} onChangeText={setPostalCode} />
          </div>

          <TextInputComp showLabel label="Active Contact Number" value={phoneNumber} onChangeText={setPhoneNumber} maxLength={12} />

          <div className="space-y-1.5">
            <label className="text-xs font-bold text-slate-500">Designation Type</label>
            <div className="flex gap-4">
              <label className="flex items-center gap-2 text-xs font-semibold text-slate-700 cursor-pointer">
                <input type="radio" name="addressType" checked={addressType === 'Home'} onChange={() => setAddressType('Home')} className="accent-[#2F5E2E] w-4 h-4" />
                <span>Home</span>
              </label>
              <label className="flex items-center gap-2 text-xs font-semibold text-slate-700 cursor-pointer">
                <input type="radio" name="addressType" checked={addressType === 'office'} onChange={() => setAddressType('office')} className="accent-[#2F5E2E] w-4 h-4" />
                <span>Office</span>
              </label>
            </div>
          </div>

          <div className="pt-2 flex gap-2">
            <CommonButton 
              text={isEditing ? "Synchronize Changes" : "Save Coordinates"} 
              variant="filled" 
              className="bg-[#2F5E2E] text-white font-black text-xs px-4 h-9 shadow-xs" 
              onClick={handleSaveOrUpdateAddressForm} 
            />
            <CommonButton 
              text="Cancel" 
              variant="outline" 
              className="text-slate-500 font-semibold text-xs px-4 h-9" 
              onClick={handleResetFormStateFields} 
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default AddressCard;